import { Box, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { brandColors, insightsMapColorsArr } from 'types/colors';
import { suggestions } from './insightsLabels.js';
const INCREASE_COLOR = '#0af0ed';
const DECREASE_COLOR = '#e74221';
const TimeInMS = {
  Second: 1000,
  Minute: 1000 * 60,
  Hour: 1000 * 60 * 60,
  Day: 1000 * 60 * 60 * 24,
  Week: 1000 * 60 * 60 * 24 * 7,
  Month: 1000 * 60 * 60 * 24 * 30,
  Year: 1000 * 60 * 60 * 24 * 365,
};

function subtractOneHour(timeString) {
  // Parse the string into hours, minutes, day, and month
  const [time, date] = timeString.split(' ');
  const [hours, minutes] = time.split(':').map(Number);
  const [day, month] = date.split('.').map(Number);

  // Create a Date object for the given time
  const now = new Date();
  const fullDate = new Date(now.getFullYear(), month - 1, day, hours, minutes);

  // Subtract one hour
  fullDate.setHours(fullDate.getHours() - 1);

  // Format back to "HH:mm dd.MM"
  const newTime = fullDate.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${newTime}`;
}

const Legend = ({ color, title }) => {
  return (
    <Box display="flex" alignItems="center" mb={2}>
      <Box
        sx={{
          width: 20,
          height: 20,
          backgroundColor: color,
          borderRadius: '4px',
          mr: 1.5,
          border: '1px solid black',
        }}
      />
      <Typography variant="body1" fontWeight="small" fontSize={'1rem'}>
        {title}
      </Typography>
    </Box>
  );
};

const ONE_DAY = 86400;

const InsightsHeatMap = ({ type, timeRangeObj, sensorsList, width }) => {
  const [key, setKey] = useState(0);
  const [loading, setLoading] = useState(true);

  // ✅ Early validation (same as HeatMap)
  if (!Array.isArray(sensorsList) || sensorsList.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          border: '1px dashed #ccc',
          borderRadius: '8px',
          margin: '20px',
        }}>
        <Typography variant="h6" color="text.secondary">
          No insights sensor data available
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Sensors list: {JSON.stringify(sensorsList)}
        </Typography>
      </Box>
    );
  }

  const forceRerender = () => {
    setKey(prevKey => prevKey + 1);
  };

  useEffect(() => {
    // forceRerender();
    setKey(prevKey => prevKey + 1);
    setLoading(false);
  }, [width]);

  let series;

  const getTimeLabels = () => {
    let resArray = [];

    if (sensorsList.length > 0 && sensorsList[0]?.data) {
      sensorsList[0].data.forEach(measurement => {
        if (measurement && measurement.unix_time) {
          resArray.push(
            `${DateTime.fromSeconds(measurement.unix_time).toFormat(`HH:mm dd.MM`)}`,
          );
        }
      });
    }

    return resArray;
  };

  const getSeries = () => {
    const series = sensorsList
      .map((sensor, index) => {
        if (sensor && (sensor.sensor_name || sensor.name)) {
          const sensorName =
            sensor.sensor_name || sensor.name || `Sensor ${index + 1}`;
          const sensorData = sensor.data || [];

          const seriesData = sensorData.map((measurement, dataIndex) => {
            const value = measurement?.insightId ?? measurement?.value;

            if (measurement && value !== undefined && value !== null) {
              return value;
            }
            console.log(
              `⚠️ Invalid insights measurement at ${dataIndex}:`,
              measurement,
            );
            return null;
          });

          return {
            name: sensorName,
            data: seriesData,
          };
        }

        console.log(`⚠️ Invalid insights sensor object at ${index}:`, sensor);
        return null;
      })
      .filter(Boolean);

    return series;
  };

  try {
    series = getSeries();

    if (!series || series.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '400px',
            border: '1px dashed #ccc',
            borderRadius: '8px',
            margin: '20px',
          }}>
          <Typography variant="h6" color="text.secondary">
            No valid insights series data available
          </Typography>
        </Box>
      );
    }
  } catch (error) {
    console.error('❌ Error generating insights series:', error);
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          border: '1px dashed #ccc',
          borderRadius: '8px',
          margin: '20px',
        }}>
        <Typography variant="h6" color="error">
          Error processing insights heat map data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {error.message}
        </Typography>
      </Box>
    );
  }

  const legendStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
    justifyContent: 'center',
  };

  // const colorBoxStyle = {
  //   width: '20px',
  //   height: '20px',
  //   display: 'inline-block',
  //   marginRight: '10px',
  //   border: '1px solid #000',
  // };

  const options = {
    chart: {
      type: 'heatmap',
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0,
        colorScale: {
          ranges: [
            { from: 0, to: 0.1, color: insightsMapColorsArr.insight0 },
            { from: 1, to: 1, color: insightsMapColorsArr.insight1 },
            { from: 2, to: 2, color: insightsMapColorsArr.insight2 },
            { from: 3, to: 3, color: insightsMapColorsArr.insight3 },
            { from: 4, to: 4, color: insightsMapColorsArr.insight4 },
            { from: 5, to: 5, color: insightsMapColorsArr.insight5 },
            { from: 6, to: 6, color: insightsMapColorsArr.insight6 },
            { from: 7, to: 7, color: insightsMapColorsArr.insight7 },
            { from: 8, to: 8, color: insightsMapColorsArr.insight8 },
            { from: 9, to: 9, color: insightsMapColorsArr.insight9 },
            { from: 10, to: 10, color: insightsMapColorsArr.insight10 },
            { from: 11, to: 11, color: insightsMapColorsArr.insight11 },
            { from: 12, to: 12, color: insightsMapColorsArr.insight12 },
            { from: 13, to: 13, color: insightsMapColorsArr.insight13 },
            { from: 9999, to: 9999, color: brandColors.PURPLE },
          ],
        },
      },
    },
    responsive: [
      {
        options: {
          chart: {
            // width: '100%',
          },
        },
      },
    ],
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      x: {
        formatter: function (val) {
          // Customize the format of the x-axis tooltip here
          return `${subtractOneHour(val)} - ${val}`;
        },
      },
      y: {
        formatter: function (val) {
          // Customize the tooltip value
          return `${suggestions[val]}`; // Example: Adds "units" after the value
        },
      },
    },
    xaxis: {
      tickAmount: 30,
      categories: getTimeLabels(),
      labels: {
        offsetX: 6,
        rotate: -50,
        rotateAlways: true,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        align: 'left',
        offsetX: 27,
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
        },
      },
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        marginTop: '-1rem',
        width: { width },
      }}>
      {loading ? (
        <Box
          sx={{
            //  ,
            height: '80%',
            display: 'flex',
            justifyContent: 'center',
          }}>
          <CircularProgress style={{ color: '#3244E2', marginTop: '200px' }} />
        </Box>
      ) : (
        <Chart
          key={key}
          options={options}
          series={series}
          type="heatmap"
          height={645}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 5,
          marginTop: '-1rem',
        }}>
        <Legend color={brandColors.PURPLE} title="Unavailable data"></Legend>
        <Legend color={DECREASE_COLOR} title="Decrease"></Legend>
        <Legend color={INCREASE_COLOR} title="Increase"></Legend>
        <Legend color="#19a439" title="No action needed"></Legend>
        <Legend color="#5df780" title="Closed systems"></Legend>
      </Box>
    </Box>
  );
};

export default InsightsHeatMap;
